import React, { useState, useRef, useEffect, type ReactNode, type CSSProperties } from 'react';
import { IconCheck, IconChevronRight, IconChevronDown, IconX, IconInfoCircle } from '@tabler/icons-react';
import { Button } from '@components/v4/Button/Button';

// ── Loading keyframes (injected once) ───────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('plick-v4-keyframes')) {
  const s = document.createElement('style');
  s.id = 'plick-v4-keyframes';
  s.textContent = `
    @keyframes plick-spin { to { transform: rotate(360deg); } }
    @keyframes plick-shimmer { from { background-position: -480px 0; } to { background-position: 480px 0; } }
    @keyframes plick-fadein { from { opacity: 0; } to { opacity: 1; } }
  `;
  document.head.appendChild(s);
}

// ── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({
  size = 16,
  color = 'currentColor',
  strokeWidth = 2.5,
}: {
  size?: number;
  color?: string;
  strokeWidth?: number;
}) => (
  <span style={{ display: 'inline-flex', width: size, height: size, color }} aria-hidden="true">
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ animation: 'plick-spin 720ms linear infinite' }}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".22" strokeWidth={strokeWidth} />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  </span>
);

// ── Skeleton ─────────────────────────────────────────────────────────────────
export const Skeleton = ({
  width = '100%',
  height = 14,
  radius = 4,
  circle = false,
  style = {},
}: {
  width?: number | string;
  height?: number;
  radius?: number;
  circle?: boolean;
  style?: CSSProperties;
}) => {
  const w = typeof width === 'number' ? `${width}px` : width;
  const h = `${height}px`;
  return (
    <span
      style={{
        display: 'block',
        width: w,
        height: circle ? w : h,
        borderRadius: circle ? '50%' : radius,
        background: 'linear-gradient(90deg, #F7F9FA 0%, #E5ECEE 50%, #F7F9FA 100%)',
        backgroundSize: '480px 100%',
        animation: 'plick-shimmer 1.4s linear infinite',
        ...style,
      }}
      aria-hidden="true"
    />
  );
};

const SkeletonField = ({ short }: { short?: boolean }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <Skeleton width={short ? 80 : 130} height={11} />
    <Skeleton height={36} radius={6} />
  </div>
);

const SectionCardSkeleton = () => (
  <div
    style={{ display: 'flex', flexDirection: 'column', gap: 18, animation: 'plick-fadein 240ms ease-out' }}
    aria-busy="true"
    aria-live="polite"
  >
    <SkeletonField />
    <SkeletonField />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      <SkeletonField short />
      <SkeletonField />
    </div>
  </div>
);

// ── Exported types ──────────────────────────────────────────────────────────
export type SectionStatus = 'complete' | 'required' | 'error' | 'pending' | 'optional' | 'live' | 'paused' | 'modified';
// `isNew`: pinta un pill "Nuevo" junto al label en la navegación de secciones. Lo gobierna el
// caller (típicamente la elegibilidad de invitación del framework de rollout) — el layout solo
// pinta. Úsalo para señalar dónde vive una funcionalidad recién ofrecida.
export type SectionDef = { id: string; label: string; status: SectionStatus; isNew?: boolean };
export type CompletionItem = { label: string; anchor: string; done: boolean };

// ── Internal config ─────────────────────────────────────────────────────────
const STATUS_CFG: Record<SectionStatus, { bg: string; fg: string; dot: string; label: string }> = {
  complete: { bg: '#EFFBF5', fg: '#06875F', dot: '#08C380', label: 'Completo' },
  pending: { bg: '#FFF8EC', fg: '#C77900', dot: '#FEAE33', label: 'Pendiente' },
  required: { bg: '#FFF5F5', fg: '#D54C4C', dot: '#FF3939', label: 'Requerido' },
  error: { bg: '#FFE4E4', fg: '#B71C1C', dot: '#E53935', label: 'Error' },
  optional: { bg: '#EDF5F7', fg: '#4298B5', dot: '#4298B5', label: 'Opcional' },
  live: { bg: '#EFFBF5', fg: '#06875F', dot: '#08C380', label: 'Activo' },
  paused: { bg: '#FFF5F5', fg: '#D54C4C', dot: '#FF3939', label: 'Pausado' },
  modified: { bg: '#FFF3CD', fg: '#856404', dot: '#FEAE33', label: 'Sin guardar' },
};

// Pill "Nuevo" de la navegación de secciones (SectionDef.isNew). Acento de marca para que
// resalte sin competir con los pills de estado (Listo/Pendiente). Texto fijo: señala
// descubrimiento, no estado.
const NewSectionPill = () => (
  <span
    style={{
      fontSize: 9.5,
      fontWeight: 800,
      letterSpacing: '.06em',
      textTransform: 'uppercase' as const,
      background: '#FEAE33',
      color: '#253746',
      borderRadius: 9999,
      padding: '2px 7px',
      flexShrink: 0,
    }}
  >
    Nuevo
  </span>
);

const STATUS_LABEL: Record<SectionStatus, { label: string; fg: string; bg: string; icon: ReactNode }> = {
  complete: {
    label: 'Listo',
    fg: '#06875F',
    bg: '#EFFBF5',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  pending: {
    label: 'Falta',
    fg: '#C77900',
    bg: '#FFF8EC',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="2.5" />
        <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  required: {
    label: 'Requerido',
    fg: '#D54C4C',
    bg: '#FFF5F5',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="currentColor" />
        <line x1="12" y1="6.5" x2="12" y2="13.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="12" cy="16.5" r="1.2" fill="white" />
      </svg>
    ),
  },
  error: {
    label: 'Error',
    fg: '#B22222',
    bg: '#FFE8E8',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M12 3 2 21h20L12 3z" fill="currentColor" />
        <line x1="12" y1="10" x2="12" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="18" r="1" fill="white" />
      </svg>
    ),
  },
  optional: {
    label: 'Opcional',
    fg: '#6B6B6B',
    bg: '#F4F4F4',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 3" />
      </svg>
    ),
  },
  live: {
    label: 'Activo',
    fg: '#06875F',
    bg: '#EFFBF5',
    icon: (
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: '#08C380',
          display: 'inline-block',
          boxShadow: '0 0 0 2px rgba(8,195,128,.2)',
        }}
      />
    ),
  },
  paused: {
    label: 'Pausado',
    fg: '#D54C4C',
    bg: '#FFF5F5',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
        <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
      </svg>
    ),
  },
  modified: {
    label: 'Sin guardar',
    fg: '#856404',
    bg: '#FFF3CD',
    icon: (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path
          d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
};

// ── SectionCard ─────────────────────────────────────────────────────────────
export type SectionCardProps = {
  id?: string;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  status?: SectionStatus;
  isMobile?: boolean;
  loading?: boolean;
  children: ReactNode;
};

export const SectionCard = ({ id, icon, title, subtitle, status, isMobile, loading, children }: SectionCardProps) => {
  const cfg = status ? STATUS_CFG[status] : null;
  const hPad = isMobile ? 14 : 20;
  return (
    <section
      id={id}
      style={{ background: '#ffffff', borderRadius: 12, border: '1px solid #E5ECEE', scrollMarginTop: 80 }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: `18px ${hPad}px 0`,
          background: '#ffffff',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 8,
            background: '#EDF5F7',
            color: '#4298B5',
            marginBottom: 18,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2
              style={{
                margin: 0,
                fontSize: isMobile ? 14 : 15,
                fontWeight: 700,
                color: '#253746',
                letterSpacing: '-0.005em',
              }}
            >
              {title}
            </h2>
            {cfg && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  fontSize: 11,
                  background: cfg.bg,
                  color: cfg.fg,
                  letterSpacing: '0.04em',
                  padding: '3px 8px 3px 6px',
                  borderRadius: 9999,
                  flexShrink: 0,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                {cfg.label}
              </span>
            )}
          </div>
          {subtitle && <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B6B6B', lineHeight: 1.5 }}>{subtitle}</p>}
        </div>
      </header>
      <div style={{ padding: `0 ${hPad}px 20px` }}>{loading ? <SectionCardSkeleton /> : children}</div>
    </section>
  );
};

// ── CompactBreakdown ─────────────────────────────────────────────────────────
const CompactBreakdown = ({ items, isMobile }: { items: CompletionItem[]; isMobile: boolean }) => (
  <div
    style={{
      width: '100%',
      marginTop: 4,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: 6,
    }}
  >
    {items.map((item) => (
      <div
        key={item.anchor}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '7px 10px',
          borderRadius: 6,
          background: '#fff',
          border: `1px solid ${item.done ? '#DCEEE3' : '#E5ECEE'}`,
          fontSize: 13,
          color: item.done ? '#06875F' : '#6B6B6B',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>
            {item.done ? '✓' : '○'} {item.label}
          </span>
        </div>
      </div>
    ))}
  </div>
);

// ── CompletionMeter ──────────────────────────────────────────────────────────
type CompletionMeterProps = {
  percent: number;
  items: CompletionItem[];
  onJumpTo: (id: string) => void;
  hasRequiredPending: boolean;
  isMobile: boolean;
  storageKey: string;
};

const CompletionMeter = ({
  percent,
  items,
  onJumpTo,
  hasRequiredPending,
  isMobile,
  storageKey,
}: CompletionMeterProps) => {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  const done = items.filter((i) => i.done).length;
  const nextPending = items.find((i) => !i.done);

  if (percent === 100) {
    return (
      <div
        style={{
          background: '#EFFBF5',
          border: '1px solid #B4ECD4',
          color: '#06875F',
          fontSize: 14,
          padding: '10px 14px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap' as const,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#08C380',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconCheck size={12} color="#ffffff" stroke={3} />
        </span>
        <strong>Perfil completo</strong>
        <span style={{ opacity: 0.75 }}>· Todas las secciones están listas</span>
        <button
          onClick={() => setBreakdownOpen(!breakdownOpen)}
          style={{
            background: 'transparent',
            border: 0,
            color: '#06875F',
            fontFamily: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
            letterSpacing: '0.03em',
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {breakdownOpen ? 'Ocultar' : 'Ver detalle'}
          <IconChevronDown
            size={12}
            style={{ transform: breakdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}
          />
        </button>
        {breakdownOpen && <CompactBreakdown items={items} isMobile={isMobile} />}
      </div>
    );
  }

  if (!hasRequiredPending) {
    if (dismissed) return null;
    return (
      <div
        style={{
          background: '#EDF5F7',
          border: '1px solid #BFD3D9',
          color: '#253746',
          fontSize: 14,
          padding: isMobile ? '10px 12px' : '12px 14px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap' as const,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: '#4298B5',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2l2.4 5.2 5.6.6-4.2 3.8 1.2 5.6L12 14.5l-5 2.7 1.2-5.6L4 7.8l5.6-.6L12 2z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <strong style={{ fontSize: 14 }}>Perfil activo</strong>
          <span style={{ opacity: 0.7, marginLeft: 6 }}>
            · {percent}% · {items.filter((i) => !i.done).length} secciones sugeridas para destacar más
          </span>
        </div>
        {nextPending && (
          <button
            onClick={() => onJumpTo(nextPending.anchor)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontWeight: 700,
              textTransform: 'uppercase' as const,
              fontSize: 12,
              padding: '5px 11px',
              background: '#ffffff',
              color: '#4298B5',
              border: '1px solid #4298B5',
              letterSpacing: '0.03em',
              cursor: 'pointer',
              fontFamily: 'inherit',
              borderRadius: 9999,
            }}
          >
            Completar {nextPending.label}
            <IconChevronRight size={10} stroke={3} />
          </button>
        )}
        <button
          onClick={() => setBreakdownOpen(!breakdownOpen)}
          style={{
            background: 'transparent',
            border: 0,
            color: '#6B6B6B',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: '0.03em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            textTransform: 'uppercase' as const,
          }}
        >
          Detalle
        </button>
        <button
          onClick={() => {
            setDismissed(true);
            try {
              localStorage.setItem(storageKey, '1');
            } catch {
              // localStorage unavailable in private mode
            }
          }}
          title="Ocultar esta sugerencia"
          style={{
            background: 'transparent',
            border: 0,
            color: '#6B6B6B',
            cursor: 'pointer',
            padding: 4,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <IconX size={14} />
        </button>
        {breakdownOpen && <CompactBreakdown items={items} isMobile={isMobile} />}
      </div>
    );
  }

  // Critical / onboarding mode — dark gradient banner
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #253746 0%, #18242E 100%)',
        color: '#ffffff',
        padding: isMobile ? 14 : 18,
        borderRadius: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8, flexWrap: 'wrap' as const }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.06em',
            opacity: 0.65,
          }}
        >
          Configurando tu perfil
        </span>
        <span style={{ fontSize: 14, opacity: 0.85, fontWeight: 500 }}>
          {done} de {items.length} secciones completas
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontWeight: 700,
            fontSize: isMobile ? 22 : 26,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {percent}
          <span style={{ fontSize: 13, opacity: 0.55, marginLeft: 2, fontWeight: 600 }}>%</span>
        </span>
      </div>
      <div
        style={{
          height: 6,
          background: 'rgba(255,255,255,0.12)',
          borderRadius: 9999,
          overflow: 'hidden',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: '#08C380',
            borderRadius: 9999,
            transition: 'width 600ms',
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
        {nextPending && (
          <button
            onClick={() => onJumpTo(nextPending.anchor)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 700,
              textTransform: 'uppercase' as const,
              fontSize: 13,
              padding: '6px 12px',
              background: '#08C380',
              color: '#ffffff',
              border: 'none',
              letterSpacing: '0.02em',
              cursor: 'pointer',
              fontFamily: 'inherit',
              borderRadius: 9999,
            }}
          >
            Continuar con: {nextPending.label}
            <IconChevronRight size={11} stroke={3} />
          </button>
        )}
        <button
          onClick={() => setBreakdownOpen(!breakdownOpen)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontWeight: 600,
            fontSize: 12,
            background: 'rgba(255,255,255,0.10)',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.16)',
            padding: '5px 11px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            borderRadius: 9999,
          }}
        >
          <IconInfoCircle size={11} />
          {breakdownOpen ? 'Ocultar detalle' : 'Cómo se calcula'}
        </button>
      </div>
      {breakdownOpen && (
        <div
          style={{
            padding: 12,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 8,
            marginTop: 12,
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8, lineHeight: 1.5 }}>
            Tu perfil suma <strong style={{ color: '#08C380' }}>{Math.round(100 / items.length)}%</strong> por cada
            sección con sus datos requeridos completos.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 6 }}>
            {items.map((item) => (
              <button
                key={item.anchor}
                onClick={() => onJumpTo(item.anchor)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: item.done ? 'rgba(8,195,128,0.12)' : 'rgba(255,255,255,0.05)',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: 13,
                  border: 0,
                  fontFamily: 'inherit',
                  textAlign: 'left' as const,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: item.done ? '#08C380' : 'rgba(255,255,255,0.18)',
                      color: '#ffffff',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    {item.done ? (
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M5 12l5 5L20 7"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    )}
                  </span>
                  <span style={{ flex: 1, fontWeight: 600 }}>{item.label}</span>
                  {!item.done && (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>Pendiente</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── DirtyNavModal ────────────────────────────────────────────────────────────
type DirtyNavModalProps = {
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
  onCancel: () => void;
};

const DirtyNavModal = ({ onSave, onDiscard, onCancel }: DirtyNavModalProps) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.resolve(onSave());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dirty-nav-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.40)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 16px',
        animation: 'plick-fadein 160ms ease-out',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 20px 60px rgba(37,55,70,0.22)',
          maxWidth: 360,
          width: '100%',
        }}
      >
        <h3
          id="dirty-nav-title"
          style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#253746', letterSpacing: '-0.01em' }}
        >
          Cambios sin guardar
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 15, color: '#6B6B6B', lineHeight: 1.55 }}>
          Tienes cambios sin guardar en esta sección. ¿Qué deseas hacer?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '10px 16px',
              background: saving ? '#2a8fa8' : '#4298B5',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving ? 'progress' : 'pointer',
              fontFamily: 'inherit',
              transition: 'background 160ms',
            }}
          >
            {saving ? <Spinner size={14} color="#ffffff" /> : null}
            Guardar
          </button>
          <button
            onClick={onDiscard}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '10px 16px',
              background: '#ffffff',
              color: '#253746',
              border: '1.5px solid #E5ECEE',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.55 : 1,
              fontFamily: 'inherit',
            }}
          >
            Descartar
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '8px 16px',
              background: 'transparent',
              color: '#6B6B6B',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.55 : 1,
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── SectionNav ───────────────────────────────────────────────────────────────
type SectionNavProps = {
  sections: SectionDef[];
  active: string;
  onSelect: (id: string) => void;
  isMobile: boolean;
  // C-10: indicador de completitud DISCRETO (p.ej. "2 de 4 listas") — el ÚNICO de la página en
  // modo 'sections', integrado en la cabecera de la lista en vez de un banner aparte.
  progressLabel?: string;
};

const SectionNav = ({ sections, active, onSelect, isMobile, progressLabel }: SectionNavProps) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const activeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isMobile && stripRef.current && activeBtnRef.current) {
      const strip = stripRef.current;
      const btn = activeBtnRef.current;
      const target = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [active, isMobile]);

  if (isMobile) {
    return (
      <div style={{ position: 'relative', margin: '0 -14px' }}>
        {/* C-10: indicador único y discreto de completitud en modo 'sections' (mobile). */}
        {progressLabel && (
          <div style={{ padding: '0 14px 4px', fontSize: 11, color: '#6B6B6B', fontWeight: 600 }}>{progressLabel}</div>
        )}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 12,
            width: 28,
            background: 'linear-gradient(to right, transparent, #F4F4F4)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <div
          ref={stripRef}
          style={
            {
              display: 'flex',
              gap: 6,
              overflowX: 'auto',
              padding: '4px 14px 12px',
              scrollbarWidth: 'none',
            } as React.CSSProperties
          }
        >
          {sections.map((s) => {
            const on = active === s.id;
            const st = STATUS_LABEL[s.status];
            return (
              <button
                key={s.id}
                ref={on ? activeBtnRef : null}
                onClick={() => onSelect(s.id)}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '7px 6px 7px 12px',
                  borderRadius: 9999,
                  background: on ? '#4298B5' : '#ffffff',
                  color: on ? '#ffffff' : '#253746',
                  border: `1.5px solid ${on ? '#4298B5' : '#E5ECEE'}`,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                {s.label}
                {s.isNew && <NewSectionPill />}
                <span
                  style={{
                    fontWeight: 700,
                    borderRadius: 9999,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    padding: '2px 6px',
                    background: on ? 'rgba(255,255,255,0.22)' : st.bg,
                    color: on ? '#ffffff' : st.fg,
                  }}
                >
                  {on ? null : st.icon}
                  {st.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 18 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          margin: '0 0 8px',
          padding: '0 12px',
        }}
      >
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            margin: 0,
            color: '#6B6B6B',
            letterSpacing: '0.04em',
          }}
        >
          Secciones
        </p>
        {/* C-10: indicador único y discreto de completitud (reemplaza el banner CompletionMeter). */}
        {progressLabel && <span style={{ fontSize: 11, color: '#6B6B6B', fontWeight: 600 }}>{progressLabel}</span>}
      </div>
      {sections.map((s) => {
        const on = active === s.id;
        const st = STATUS_LABEL[s.status];
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              padding: '9px 10px',
              borderRadius: 8,
              background: on ? '#ffffff' : 'transparent',
              color: '#253746',
              fontWeight: on ? 600 : 500,
              border: 0,
              borderLeft: `3px solid ${on ? '#4298B5' : 'transparent'}`,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left' as const,
              width: '100%',
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: on ? '#253746' : '#6B6B6B',
              }}
            >
              {s.label}
              {s.isNew && <NewSectionPill />}
            </span>
            <span
              style={{
                fontWeight: 700,
                textTransform: 'uppercase' as const,
                fontSize: 10,
                padding: s.status === 'complete' ? '3px 4px' : '2px 6px',
                borderRadius: 9999,
                background: st.bg,
                color: st.fg,
                letterSpacing: '0.04em',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                minWidth: s.status === 'complete' ? 16 : 'auto',
              }}
            >
              {st.icon}
              {s.status !== 'complete' && st.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

// ── SaveBar ──────────────────────────────────────────────────────────────────
type SaveBarProps = {
  dirty: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  isLoading?: boolean;
  saving?: boolean;
  isMobile?: boolean;
  hasErrors?: boolean;
};

// Fila de estado del SaveBar (icono + copy). Extraída para mantener baja la complejidad.
const SaveBarStatus = ({
  isMobile,
  hasErrors,
  isSaving,
}: {
  isMobile?: boolean;
  hasErrors?: boolean;
  isSaving: boolean;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: isMobile ? 'initial' : 1, minWidth: 0 }}>
    {isSaving ? (
      <Spinner size={14} color="#ffffff" />
    ) : (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: hasErrors ? '#FF3939' : '#FEAE33',
          flexShrink: 0,
        }}
      />
    )}
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, fontWeight: 700, lineHeight: 1.3 }}>
        {isSaving ? 'Guardando cambios…' : hasErrors ? 'Corrige los errores antes de guardar' : 'Cambios sin guardar'}
      </p>
      <p
        style={{
          margin: '1px 0 0',
          fontSize: isMobile ? 10.5 : 11,
          opacity: 0.75,
          lineHeight: 1.4,
          // En móvil se oculta el helper largo cuando no hay error/guardado: es la causa
          // principal del amontonamiento en pantallas chicas (el copy partía en muchas líneas).
          display: isMobile && !hasErrors && !isSaving ? 'none' : 'block',
        }}
      >
        {isSaving
          ? 'No cierres esta ventana — termina en un momento'
          : hasErrors
            ? isMobile
              ? 'Revisa las secciones en rojo del menú'
              : 'Revisa las secciones marcadas en rojo en el menú lateral'
            : 'Se aplican a todas las secciones de esta página al confirmar'}
      </p>
    </div>
  </div>
);

// Fila de acciones del SaveBar (Descartar + Guardar). En móvil ocupa su propia línea y
// el botón Guardar crece (flex: 1) en vez de quedar comprimido junto al copy.
const SaveBarActions = ({
  onSave,
  onDiscard,
  isMobile,
  hasErrors,
  isSaving,
  saveDisabled,
}: {
  onSave: () => void;
  onDiscard?: () => void;
  isMobile?: boolean;
  hasErrors?: boolean;
  isSaving: boolean;
  saveDisabled: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 8 : 6,
      justifyContent: isMobile ? 'stretch' : 'flex-end',
    }}
  >
    {onDiscard && (
      <button
        onClick={onDiscard}
        disabled={isSaving}
        style={{
          background: 'transparent',
          color: '#ffffff',
          border: isMobile ? '1px solid rgba(255,255,255,.3)' : 'none',
          borderRadius: isMobile ? 8 : 0,
          padding: isMobile ? '9px 14px' : '6px 10px',
          cursor: isSaving ? 'default' : 'pointer',
          opacity: isSaving ? 0.45 : 1,
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          textTransform: isMobile ? ('uppercase' as const) : ('none' as const),
          letterSpacing: isMobile ? '0.03em' : 0,
        }}
      >
        Descartar
      </button>
    )}
    <button
      onClick={isSaving ? undefined : onSave}
      disabled={saveDisabled}
      aria-busy={isSaving || undefined}
      title={hasErrors ? 'Corrige los errores antes de guardar' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        fontSize: 13,
        padding: isMobile ? '10px 16px' : '8px 14px',
        minWidth: isMobile ? 'auto' : 96,
        flex: isMobile ? 1 : 'initial',
        background: hasErrors ? '#5a6e7c' : '#1FC49A',
        color: '#ffffff',
        border: 'none',
        letterSpacing: '0.03em',
        cursor: saveDisabled ? (isSaving ? 'progress' : 'not-allowed') : 'pointer',
        fontFamily: 'inherit',
        opacity: hasErrors ? 0.7 : 1,
        borderRadius: 8,
        transition: 'background 160ms',
      }}
    >
      {isSaving ? (
        <>
          <Spinner size={13} color="#ffffff" />
          Guardando
        </>
      ) : (
        <>
          <IconCheck size={13} color="#ffffff" />
          Guardar
        </>
      )}
    </button>
  </div>
);

export const SaveBar = ({ dirty, onSave, onDiscard, isLoading, saving, isMobile, hasErrors }: SaveBarProps) => {
  const isSaving = Boolean(saving || isLoading);
  if (!dirty && !isSaving) return null;
  const saveDisabled = Boolean(hasErrors || isSaving);
  return (
    <div
      style={
        isMobile
          ? { position: 'fixed', bottom: 60, left: 12, right: 12, zIndex: 30 }
          : { position: 'sticky', top: 0, zIndex: 25, marginBottom: 14 }
      }
    >
      <div
        style={{
          background: '#253746',
          color: '#ffffff',
          borderTop: '2px solid #1FC49A',
          boxShadow: '0 6px 16px rgba(37,55,70,.18)',
          borderRadius: isMobile ? 10 : 12,
          padding: isMobile ? '10px 12px' : '12px 16px',
          display: 'flex',
          // En móvil apila: fila de estado arriba, fila de acciones abajo — así el copy y los
          // botones tienen cada uno su línea y nada se amontona en pantallas chicas.
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 10 : 12,
          animation: 'plick-slide-up 220ms ease-out',
        }}
      >
        <SaveBarStatus isMobile={isMobile} hasErrors={hasErrors} isSaving={isSaving} />
        <SaveBarActions
          onSave={onSave}
          onDiscard={onDiscard}
          isMobile={isMobile}
          hasErrors={hasErrors}
          isSaving={isSaving}
          saveDisabled={saveDisabled}
        />
      </div>
    </div>
  );
};

// ── FocusedWizardNav ─────────────────────────────────────────────────────────
type FocusedWizardNavProps = {
  sections: SectionDef[];
  active: string;
  onSelect: (id: string) => void;
};

const FocusedWizardNav = ({ sections, active, onSelect }: FocusedWizardNavProps) => {
  const required = sections.filter((s) => s.status !== 'optional' && s.status !== 'live' && s.status !== 'paused');
  const idx = sections.findIndex((s) => s.id === active);
  const requiredIdx = required.findIndex((s) => s.id === active);
  const total = required.length;
  const isOptional = requiredIdx === -1;
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx < sections.length - 1 ? sections[idx + 1] : null;

  // When in an optional section, find how many required sections precede it
  // so the bar stays at the progress of the last completed required step
  const lastRequiredBeforeOptional = isOptional
    ? sections.slice(0, idx).filter((s) => s.status !== 'optional').length
    : 0;
  const progressPct = isOptional ? (lastRequiredBeforeOptional / total) * 100 : ((requiredIdx + 1) / total) * 100;

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #E5ECEE',
        borderRadius: 12,
        padding: '12px 16px',
        marginBottom: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap' as const,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span
          style={{
            fontSize: 11,
            color: '#6B6B6B',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
          }}
        >
          {isOptional ? 'Sección opcional' : `Paso ${requiredIdx + 1} de ${total}`}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#253746' }}>{sections[idx]?.label}</span>
      </div>
      <div style={{ flex: 1, minWidth: 200, height: 6, background: '#E5ECEE', borderRadius: 3, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${progressPct}%`,
            background: '#4298B5',
            transition: 'width 220ms',
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="outline" size="sm" onClick={() => prev && onSelect(prev.id)} disabled={!prev}>
          ← Atrás
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => next && onSelect(next.id)}
          disabled={!next}
          icon={<IconChevronRight size={12} />}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

// ── EditPageLayout ───────────────────────────────────────────────────────────
export type EditPageLayoutProps = {
  isMobile: boolean;
  viewport?: number;
  breadcrumb?: string[];
  title: string;
  subtitle?: string;
  sections: SectionDef[];
  activeSection: string;
  onSectionChange: (id: string) => void;
  completionItems?: CompletionItem[];
  completionPercent?: number;
  // C-10: modelo de progreso. 'wizard' (default) = el comportamiento histórico (meter + stepper
  // "Paso X de N" + lista lateral) para flujos secuenciales tipo onboarding. 'sections' = para
  // páginas de PREFERENCIAS independientes (Ajustes): UN solo modelo — la lista lateral de
  // secciones con su badge + un indicador de completitud DISCRETO en su cabecera; se suprime el
  // banner CompletionMeter y el stepper Atrás/Siguiente (secuencialidad falsa sobre secciones
  // opcionales). Esto también descomprime la cabecera (C-25).
  progressMode?: 'wizard' | 'sections';
  dirty: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  isLoading?: boolean;
  hasErrors?: boolean;
  children: ReactNode;
};

export const EditPageLayout = ({
  isMobile,
  viewport,
  breadcrumb,
  title,
  subtitle,
  sections,
  activeSection,
  onSectionChange,
  completionItems,
  completionPercent,
  progressMode = 'wizard',
  dirty,
  onSave,
  onDiscard,
  isLoading,
  hasErrors,
  children,
}: EditPageLayoutProps) => {
  // C-10: en modo 'sections' la lista lateral es el ÚNICO sistema de progreso. Suprime el
  // banner CompletionMeter y el stepper Atrás/Siguiente para no apilar tres metáforas (%, paso,
  // lista) que dicen lo mismo.
  const sectionsMode = progressMode === 'sections';
  const doneCount = completionItems ? completionItems.filter((i) => i.done).length : 0;
  const totalCount = completionItems?.length ?? 0;
  // Tablet (1024–1279): use chip strip + hide side rail, same as mobile.
  // Detect window width when viewport prop is not provided.
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    if (typeof viewport === 'number') return;
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [viewport]);
  const effectiveViewport = typeof viewport === 'number' ? viewport : windowWidth;
  const isCompact = !isMobile && effectiveViewport < 1280;
  const useChipNav = isMobile || isCompact;
  const hasRequiredPending = sections.some((s) => s.status === 'required' || s.status === 'error');

  // Auto-derive hasErrors from sections if not explicitly provided
  const derivedHasErrors = sections.some((s) => s.status === 'error');
  const effectiveHasErrors = hasErrors ?? derivedHasErrors;

  // ── Dirty-state navigation guard ──────────────────────────────────────────
  const [pendingNavTarget, setPendingNavTarget] = useState<string | null>(null);

  const handleSectionSelect = (id: string) => {
    if (dirty && id !== activeSection) {
      setPendingNavTarget(id);
    } else {
      onSectionChange(id);
    }
  };

  const handleModalSave = async () => {
    await Promise.resolve(onSave());
    if (pendingNavTarget) {
      onSectionChange(pendingNavTarget);
      setPendingNavTarget(null);
    }
  };

  const handleModalDiscard = () => {
    if (onDiscard) onDiscard();
    if (pendingNavTarget) {
      onSectionChange(pendingNavTarget);
      setPendingNavTarget(null);
    }
  };

  const handleModalCancel = () => {
    setPendingNavTarget(null);
  };

  const renderBreadcrumb = () => {
    if (!breadcrumb) return null;
    return breadcrumb.map((seg, i, arr) => (
      <span key={i}>
        {i > 0 && <span style={{ opacity: 0.5, margin: '0 4px' }}>/</span>}
        <span
          style={{
            color: i === arr.length - 1 ? '#253746' : 'inherit',
            opacity: i === arr.length - 1 ? 1 : 0.6,
            fontWeight: i === arr.length - 1 ? 600 : 400,
          }}
        >
          {seg}
        </span>
      </span>
    ));
  };

  return (
    <div
      style={{
        padding: isMobile ? '12px 12px 90px' : '20px 24px',
        background: '#F4F4F4',
        minHeight: '100%',
        position: 'relative',
      }}
    >
      {/* Header — title + subtitle. NO save button here (SaveBar owns save). */}
      <div style={{ marginBottom: isMobile ? 12 : 14 }}>
        {!isMobile && breadcrumb && (
          <div style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 4 }}>{renderBreadcrumb()}</div>
        )}
        <h2
          style={{
            margin: 0,
            fontSize: isMobile ? 20 : 22,
            color: '#253746',
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h2>
        {subtitle && <div style={{ fontSize: 14, color: '#6B6B6B', marginTop: 4, lineHeight: 1.5 }}>{subtitle}</div>}
      </div>

      {/* CompletionMeter — solo en modo wizard (en 'sections' el progreso lo lleva la lista). */}
      {!sectionsMode && completionItems && typeof completionPercent === 'number' && (
        <CompletionMeter
          percent={completionPercent}
          items={completionItems}
          onJumpTo={handleSectionSelect}
          hasRequiredPending={hasRequiredPending}
          isMobile={isMobile}
          storageKey={`plick:meter-soft-dismissed:${sections[0]?.id ?? 'default'}`}
        />
      )}

      {/* Chip strip — mobile and compact tablet */}
      {useChipNav && (
        <SectionNav
          sections={sections}
          active={activeSection}
          onSelect={handleSectionSelect}
          isMobile
          progressLabel={sectionsMode ? `${doneCount} de ${totalCount} listas` : undefined}
        />
      )}

      {/* Wizard nav — full desktop only, y solo en modo wizard (C-10: el stepper impone
          secuencialidad falsa sobre secciones independientes de Ajustes). */}
      {!sectionsMode &&
        !useChipNav &&
        sections.filter((s) => s.status !== 'optional' && s.status !== 'live' && s.status !== 'paused').length > 1 && (
          <FocusedWizardNav sections={sections} active={activeSection} onSelect={handleSectionSelect} />
        )}

      {/* Desktop SaveBar — full-width, sticky at viewport top, above the grid */}
      {!useChipNav && (
        <SaveBar
          dirty={dirty}
          onSave={onSave}
          onDiscard={onDiscard}
          isLoading={isLoading}
          hasErrors={effectiveHasErrors}
        />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: useChipNav ? '1fr' : '200px 1fr',
          gap: useChipNav ? 14 : 18,
          alignItems: 'start',
        }}
      >
        {!useChipNav && (
          <SectionNav
            sections={sections}
            active={activeSection}
            onSelect={handleSectionSelect}
            isMobile={false}
            progressLabel={sectionsMode ? `${doneCount} de ${totalCount} listas` : undefined}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>{children}</div>
      </div>

      {/* Floating save bar — mobile and compact tablet */}
      {useChipNav && (
        <SaveBar
          dirty={dirty}
          onSave={onSave}
          onDiscard={onDiscard}
          isLoading={isLoading}
          hasErrors={effectiveHasErrors}
          isMobile
        />
      )}

      {/* Dirty-nav confirmation modal */}
      {pendingNavTarget !== null && (
        <DirtyNavModal onSave={handleModalSave} onDiscard={handleModalDiscard} onCancel={handleModalCancel} />
      )}

      <style>{`
        @keyframes plick-slide-up { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};
